import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles, Monitor, Code2, Cpu } from 'lucide-react';
import { companyData } from '../mock';

const Hero = () => {
  return (
    <section className="relative min-h-screen flex items-center justify-center pt-28 pb-20 overflow-hidden bg-slate-900">
      {/* Premium Office Desk Background Image */}
      <div className="absolute inset-0 w-full h-full z-0">
        <img
          src="/office_bg.jpg"
          onError={(e) => {
            e.target.src = "https://images.unsplash.com/photo-1497366216548-37526070297c?w=1920&q=80";
          }}
          alt="Premium Office Desk Workspace"
          className="w-full h-full object-cover object-center scale-105"
        />
        {/* Soft Ambient White Glass Overlay */}
        <div className="absolute inset-0 bg-white/75 backdrop-blur-[2px]" />
        {/* Ambient Gradient Vignette */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/90 via-white/60 to-white/90" />
      </div>

      {/* Hero Content Overlay */}
      <div className="max-w-7xl mx-auto px-6 relative z-10 text-center">
        {/* Top Tagline Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center space-x-2 px-4 py-2 rounded-full glass mb-6 border border-purple-200 shadow-md bg-white/80"
        >
          <Sparkles size={16} className="text-purple-600 animate-pulse" />
          <span className="text-xs font-bold tracking-wide uppercase text-purple-700">
            {companyData.tagline}
          </span>
        </motion.div>

        {/* Main Headline */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-4xl sm:text-6xl md:text-7xl font-extrabold font-display text-slate-900 leading-tight tracking-tight mb-6 max-w-4xl mx-auto"
        >
          Premium Executive <span className="text-gradient">Digital Solutions</span> <br className="hidden sm:inline" />
          & Enterprise Web Apps
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-slate-700 text-base sm:text-lg md:text-xl max-w-2xl mx-auto mb-10 leading-relaxed font-normal"
        >
          {companyData.description}
        </motion.p>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16"
        >
          <a
            href="#services"
            className="w-full sm:w-auto px-8 py-4 rounded-full font-bold text-white bg-gradient-to-r from-purple-600 via-indigo-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 shadow-xl shadow-purple-500/25 transition-all duration-300 flex items-center justify-center space-x-2 group hover:scale-105"
          >
            <span>Explore Our Services</span>
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </a>

          <a
            href="#careers"
            className="w-full sm:w-auto px-8 py-4 rounded-full font-bold text-slate-800 glass hover:text-purple-700 hover:border-purple-300 transition-all duration-300 flex items-center justify-center space-x-2 hover:scale-105 shadow-md bg-white/90"
          >
            <span>We’re Hiring! Join Us</span>
          </a>
        </motion.div>

        {/* Live Metrics Grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.4 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto"
        >
          {[
            { label: 'Client Satisfaction', value: '99%', icon: Sparkles },
            { label: 'Workstations Built', value: '150+', icon: Monitor },
            { label: 'Performance Speed', value: '100/100', icon: Cpu },
            { label: 'Support & Uptime', value: '24/7', icon: Code2 },
          ].map((stat, idx) => (
            <div key={idx} className="p-4 rounded-2xl glass-card text-left bg-white/85 shadow-md border border-slate-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-2xl font-bold font-display text-slate-900">{stat.value}</span>
                <stat.icon size={18} className="text-purple-600" />
              </div>
              <p className="text-xs text-slate-600 font-semibold">{stat.label}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
